import { useRef, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import JobProgress from '@/components/jobs/JobProgress';
import VideoTimeline from '@/components/video/VideoTimeline';
import ClipsList from '@/components/video/ClipsList';
import { useJobStatus } from '@/hooks/useJobStatus';
import { useJobs } from '@/hooks/useJobs';
import { storage } from '@/lib/firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import type { ClipSegment, ReviewStatus } from '@/types/job';

export default function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { job, loading, error } = useJobStatus(id ?? null);
  const { deleteJob, reviewClip } = useJobs();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [activeSegment, setActiveSegment] = useState<ClipSegment | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadSourceVideo = useCallback(
    async (path: string) => {
      if (videoUrl) return;
      setVideoLoading(true);
      try {
        const storageRef = ref(storage, path);
        const url = await getDownloadURL(storageRef);
        setVideoUrl(url);
      } catch {
        // Video may not be accessible
      } finally {
        setVideoLoading(false);
      }
    },
    [videoUrl],
  );

  const handleSeek = useCallback((time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  }, []);

  const handleSegmentClick = useCallback((segment: ClipSegment) => {
    setActiveSegment(segment);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!id || !confirm('Delete this job and all its clips?')) return;
    setDeleting(true);
    try {
      await deleteJob(id);
      navigate('/jobs');
    } catch {
      setDeleting(false);
    }
  }, [id, deleteJob, navigate]);

  const handleReview = useCallback(
    async (clipIndex: number, status: ReviewStatus) => {
      if (!id) return;
      try {
        await reviewClip(id, clipIndex, status);
      } catch (err) {
        console.error('Failed to update clip:', err);
      }
    },
    [id, reviewClip],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-4">
        <Button variant="ghost" asChild>
          <Link to="/jobs">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Jobs
          </Link>
        </Button>
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{error ?? 'Job not found'}</p>
        </div>
      </div>
    );
  }

  const hasSegments = job.segments && job.segments.length > 0;

  if (hasSegments && !videoUrl && !videoLoading) {
    loadSourceVideo(job.sourceVideoPath);
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" asChild>
          <Link to="/jobs">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Jobs
          </Link>
        </Button>
        <Button variant="outline" size="sm" onClick={handleDelete} disabled={deleting} className="text-destructive">
          <Trash2 className="h-4 w-4 mr-1" />
          {deleting ? 'Deleting...' : 'Delete Job'}
        </Button>
      </div>

      <JobProgress job={job} />

      {hasSegments && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Scene Timeline</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {videoUrl && (
              <video
                ref={videoRef}
                src={videoUrl}
                controls
                className="w-full rounded-lg bg-black aspect-video"
                preload="metadata"
                onTimeUpdate={() => {
                  if (videoRef.current) {
                    setCurrentTime(videoRef.current.currentTime);
                  }
                }}
                onLoadedMetadata={() => {
                  if (videoRef.current) {
                    setVideoDuration(videoRef.current.duration);
                  }
                }}
              />
            )}

            {videoLoading && (
              <div className="flex items-center justify-center h-48 bg-muted rounded-lg">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {videoDuration > 0 && (
              <VideoTimeline
                segments={job.segments}
                videoDuration={videoDuration}
                currentTime={currentTime}
                onSeek={handleSeek}
                onSegmentClick={handleSegmentClick}
              />
            )}

            {activeSegment && (
              <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                Active scene: <span className="font-medium">{activeSegment.label}</span>
                {' — '}in: {activeSegment.startTime.toFixed(2)}s, out: {activeSegment.endTime.toFixed(2)}s (
                {(activeSegment.endTime - activeSegment.startTime).toFixed(2)}s duration)
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {job.status === 'completed' && job.clips && job.clips.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Review Extracted Clips</h2>
          <p className="text-sm text-muted-foreground">
            Approve clips that should proceed to VFX replacement. Reject false positives.
          </p>
          <ClipsList clips={job.clips} onReview={handleReview} />
        </div>
      )}
    </div>
  );
}
